import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import type {
    CadastroMedicoTransferenciaDto,
    CentralTransferenciaDto,
    CorrespondenciaHtmlDto,
    MedicoValidacaoTransferenciaDto,
    TipoSolicitacaoTransferencia
} from '@/core/api/sicom-api/registro/medico/central-transferencia/central-transferencia.model';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';

@Injectable({ providedIn: 'root' })
export class CentralTransferenciaService extends BaseService {
    readonly PATH = SICOM_PATHS.registro.medico.centralTransferencia.base;

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    listarAbertosOrigem(ufOrigem: string, context?: HttpContext): Observable<CentralTransferenciaDto[]> {
        return this.http.get<CentralTransferenciaDto[]>(`${this.PATH}/abertos-origem/${ufOrigem}`, { context });
    }

    listarAbertosDestino(ufDestino: string, context?: HttpContext): Observable<CentralTransferenciaDto[]> {
        return this.http.get<CentralTransferenciaDto[]>(`${this.PATH}/abertos-destino/${ufDestino}`, { context });
    }

    listarParaEncerrar(ufOrigem: string, context?: HttpContext): Observable<CentralTransferenciaDto[]> {
        return this.http.get<CentralTransferenciaDto[]>(`${this.PATH}/para-encerrar/${ufOrigem}`, { context });
    }

    validarTransferencia(crm: string, ufOrigem: string, context?: HttpContext): Observable<MedicoValidacaoTransferenciaDto> {
        return this.http.get<MedicoValidacaoTransferenciaDto>(`${this.PATH}/validar-transferencia/${crm}/${ufOrigem}`, { context });
    }

    solicitar(crm: string, ufOrigem: string, ufDestino: string, tipoSolicitacao: TipoSolicitacaoTransferencia, context?: HttpContext): Observable<CentralTransferenciaDto> {
        return this.http.get<CentralTransferenciaDto>(`${this.PATH}/solicitar/${crm}/${ufOrigem}/${ufDestino}/${tipoSolicitacao}`, { context });
    }

    cadastrarMedicoPorIdSolicitacao(idSolicitacao: number, context?: HttpContext): Observable<CadastroMedicoTransferenciaDto> {
        return this.http.get<CadastroMedicoTransferenciaDto>(`${this.PATH}/cadastro-medico/${idSolicitacao}`, { context });
    }

    confirmar(idSolicitacao: number, crm: string, uf: string, crmGerado: string, context?: HttpContext): Observable<CentralTransferenciaDto> {
        return this.http.get<CentralTransferenciaDto>(`${this.PATH}/confirmar/${idSolicitacao}/${crm}/${uf}/${crmGerado}`, { context });
    }

    repudiar(idSolicitacao: number, crm: string, uf: string, context?: HttpContext): Observable<CentralTransferenciaDto> {
        return this.http.get<CentralTransferenciaDto>(`${this.PATH}/repudiar/${idSolicitacao}/${crm}/${uf}`, { context });
    }

    encerrar(idSolicitacao: number, crm: string, uf: string, context?: HttpContext): Observable<CentralTransferenciaDto> {
        return this.http.get<CentralTransferenciaDto>(`${this.PATH}/encerrar/${idSolicitacao}/${crm}/${uf}`, { context });
    }

    listarPorCpf(cpf: string, context?: HttpContext): Observable<CentralTransferenciaDto[]> {
        return this.http.get<CentralTransferenciaDto[]>(`${this.PATH}/solicitacoes/${cpf}`, { context });
    }

    listarPorId(idSolicitacao: number, context?: HttpContext): Observable<CentralTransferenciaDto> {
        return this.http.get<CentralTransferenciaDto>(`${this.PATH}/solicitacao/${idSolicitacao}`, { context });
    }

    getCorrespondenciaHtml(idSolicitacao: number, context?: HttpContext): Observable<CorrespondenciaHtmlDto> {
        return this.http.get<CorrespondenciaHtmlDto>(`${this.PATH}/correspondencia/${idSolicitacao}`, { context });
    }

    getCertificadoRegularidadePdf(idSolicitacao: number, assinante = '', emissor = '', inSexoEmissor = 'M', context?: HttpContext): Observable<Blob> {
        const params = new HttpParams().set('assinante', assinante).set('emissor', emissor).set('inSexoEmissor', inSexoEmissor);
        return this.http.get(`${SICOM_PATHS.registro.medico.reports.certificadoRegularidade}/from-solicitacao/${idSolicitacao}`, { params, responseType: 'blob', context });
    }

    getOficioRepudioPdf(idSolicitacao: number, assinante = '', emissor = '', inSexoEmissor = 'M', context?: HttpContext): Observable<Blob> {
        const params = new HttpParams().set('assinante', assinante).set('emissor', emissor).set('inSexoEmissor', inSexoEmissor);
        return this.http.get(`${SICOM_PATHS.registro.medico.reports.oficioRepudio}/from-solicitacao/${idSolicitacao}`, { params, responseType: 'blob', context });
    }
}
